import React, { useEffect, useMemo } from 'react';
import { useAppDispatch } from '../../hook/useAppDispatch';
import { useAppSelector } from '../../hook/useAppSelector';
import Table from '../ContentWrapper/Common/Table/Table';
import FallbackLoader from '../ContentWrapper/Content/Loader/FallbackLoader';

import { setActivePageKey, setColumns } from '../../store/slice/config/config';

import { applySearchInput } from '../../store/slice/stats-slice';

import { SEARCH_INPUT_DEBOUNCE_DELAY, STATISTIC_PAGE } from '../../constant/constants';
import { searchStats } from '../../interface/stats/StatsResponse';
import { useResolvedTableState } from '../../hook/useResolvedTableState';
import { formatDeviceType, formatPaymentType } from '../../util/valueFormatters';
import { resolvePaymentTypeVisibility } from '../../util/payment-type-helper';
import { useStatsFetch } from '../../hook/useStatsFetch';

const StatisticsPage: React.FC = () => {
  const dispatch = useAppDispatch();

  const statsData = useAppSelector((state) => state.data.statsData);
  const searchInput = useAppSelector((state) => state.searchSlice.input);
  const loading = useAppSelector((state) => state.data.loading);
  const error = useAppSelector((state) => state.data.error);
  const filters = useAppSelector((state) => state.globalFilters);
  const selectedDevices = filters.PaymentSource || [];
  const selectedPaymentTypes = filters.PaymentType || [];

  const { showDevice, showDeviceType } = useResolvedTableState(selectedDevices, selectedPaymentTypes);
  const { showPaymentType } = resolvePaymentTypeVisibility({
    selectedPaymentTypes,
  });
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (searchInput !== null) {
        dispatch(applySearchInput(searchInput));
      }
    }, SEARCH_INPUT_DEBOUNCE_DELAY);

    return () => clearTimeout(timeout);
  }, [dispatch, searchInput]);

  useStatsFetch();

  useEffect(() => {
    dispatch(setActivePageKey(STATISTIC_PAGE));
  }, [dispatch]);

  useEffect(() => {
    if (statsData?.Items?.length > 0) {
      dispatch(
        setColumns({
          columns: Object.keys(statsData.Items[0]),
          pageKey: STATISTIC_PAGE,
          showDevice: showDevice,
          showDeviceType: showDeviceType,
          showPaymentType: showPaymentType,
        }),
      );
    }
    // eslint-disable-next-line
  }, [dispatch, statsData.Items]);

  const filteredItems = useMemo(() => {
    return searchStats(statsData.Items || [], searchInput);
  }, [statsData, searchInput]);

  const formattedItems = useMemo(() => {
    return (filteredItems.Items || []).map((item) => ({
      ...item,
      DeviceTypeEnum: formatDeviceType(item.DeviceTypeEnum),
      PaymentTypeEnum: formatPaymentType(item.PaymentTypeEnum),
    }));
  }, [filteredItems]);

  return (
    <>
      {loading && <FallbackLoader />}
      <Table
        data={formattedItems}
        error={error ? error : ''}
        dataLength={filteredItems.Items.length}
        showFooter={true}
        loaderIsShown={loading}
      />
    </>
  );
};

export default StatisticsPage;
